/**
 * Members tab showing users with this role
 * Read-only view - roles are assigned through user/team management
 */
import { Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";

interface RoleMembersTabProps {
	role: {
		id: string;
		name: string;
	};
}

/**
 * Members tab showing who has this role
 * This is read-only - use the Users tab to change role assignments
 * @param root0 - Component props
 * @param root0.role - The role to display members for
 */
export function RoleMembersTab({
	role,
}: RoleMembersTabProps) {
	// Fetch users to find those with this role
	const usersQuery = trpc.permissionGroups.listAccountUsers.useQuery({
		limit: 100,
		offset: 0,
	});

	if (usersQuery.isLoading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
			</div>
		);
	}

	// Find users with this role at account level
	const usersWithAccountRole = usersQuery.data?.users.filter(
		(user) => user.accountRole.id === role.id
	) ?? [];

	// Find users with this role at team level (may overlap with account role users)
	const usersWithTeamRole = usersQuery.data?.users.filter(
		(user) => user.teams.some((t) => t.role.id === role.id)
	) ?? [];

	// Unique users with this role
	const uniqueUserIds = new Set([
		...usersWithAccountRole.map((u) => u.id),
		...usersWithTeamRole.map((u) => u.id),
	]);

	const totalMemberCount = uniqueUserIds.size;

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				Users with the <strong>{role.name}</strong> role. To change role assignments, edit the user from the Users tab.
			</p>

			{totalMemberCount === 0 ? (
				<div className="flex flex-col items-center justify-center py-8 border rounded-md">
					<Users className="h-8 w-8 text-muted-foreground mb-2" />
					<p className="text-muted-foreground text-sm">No users have this role</p>
				</div>
			) : (
				<>
					{/* Users with this as account role */}
					{usersWithAccountRole.length > 0 && (
						<div className="space-y-2">
							<Label>Account Role ({usersWithAccountRole.length})</Label>
							<div className="border rounded-md divide-y">
								{usersWithAccountRole.map((user) => (
									<div
										key={user.id}
										className="flex items-center justify-between p-3"
									>
										<div>
											<div className="font-medium">
												{user.name ?? user.email}
											</div>
											{user.name && (
												<div className="text-sm text-muted-foreground">
													{user.email}
												</div>
											)}
										</div>
										<Badge variant="outline" className="text-xs">
											Account
										</Badge>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Users with this as team role */}
					{usersWithTeamRole.length > 0 && (
						<div className="space-y-2">
							<Label>Team Role ({usersWithTeamRole.length})</Label>
							<div className="border rounded-md divide-y">
								{usersWithTeamRole.map((user) => {
									const teamsWithRole = user.teams.filter((t) => t.role.id === role.id);
									return (
										<div
											key={user.id}
											className="flex items-center justify-between p-3"
										>
											<div>
												<div className="font-medium">
													{user.name ?? user.email}
												</div>
												{user.name && (
													<div className="text-sm text-muted-foreground">
														{user.email}
													</div>
												)}
											</div>
											<div className="flex flex-wrap gap-1">
												{teamsWithRole.map((team) => (
													<Badge key={team.id} variant="secondary" className="text-xs">
														{team.name}
													</Badge>
												))}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}
